import {useMemo, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {Dialog, DialogContent, DialogHeader, DialogTitle} from '@/components/ui/dialog'
import {Input} from '@/components/ui/input'
import {Skeleton} from '@/components/ui/skeleton'
import {PaginationBar} from '@/components/ui/pagination-bar'
import {DataTable, type Column} from '@/components/data-table'
import {FormDialogFooter} from '@/components/form-dialog-footer'
import {useAllEmployees, type Employee} from '@/hooks/empresa/identidad/use-employees'
import type {BateriaPaquete} from '@/hooks/bateria/use-bateria'
import {useReasignarPaquete} from '@/hooks/bateria/use-bateria-ciclo-vida'
import {suggestForma} from '@/lib/bateria'
import {translateApiError} from '@/lib/errors'
import {normalize} from '@/lib/utils'
import {tope} from '@/lib/campos-texto'

const PAGE_SIZE = 10

function validateSelection(selected: string | null): string | null {
  return selected ? null : 'admin.bateria.reasignar.selectRequired'
}

/** The paper's forma never changes: a target whose cargo suggests another forma is warned, not
 * blocked. Admin-only, never visible to the tenant. */
export function ReasignarPaqueteDialog({
  businessId,
  evaluacionId,
  paquete,
  open,
  onOpenChange,
}: {
  businessId: string
  evaluacionId: string
  paquete: BateriaPaquete
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const {t} = useTranslation()
  const {data: roster} = useAllEmployees(open ? businessId : undefined)
  const reasignar = useReasignarPaquete(businessId, evaluacionId)
  const [selected, setSelected] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const [error, setError] = useState<string | null>(null)

  const candidates = useMemo(
    () => (roster ?? []).filter((e) => e.status === 'active' && e.id !== paquete.employee_id),
    [roster, paquete.employee_id]
  )
  const matching = useMemo(() => {
    const needle = normalize(search)
    if (!needle) return candidates
    return candidates.filter((e) => normalize(`${e.name} ${e.last_name} ${e.cedula}`).includes(needle))
  }, [candidates, search])
  const total = matching.length
  const rows = useMemo(() => matching.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE), [matching, page])

  const target = candidates.find((e) => e.id === selected)
  const formaMismatch = target ? suggestForma(target, paquete.forma) !== paquete.forma : false

  function close() {
    setSelected(null)
    setSearch('')
    setPage(1)
    setError(null)
    reasignar.reset()
    onOpenChange(false)
  }

  function submit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    if (!selected) {
      setError(t(validateSelection(selected)!))
      return
    }
    reasignar.mutate(
      {paqueteId: paquete.id, employeeId: selected},
      {onSuccess: close, onError: (err) => setError(translateApiError(err))}
    )
  }

  const columns: Column<Employee>[] = [
    {
      key: 'employee',
      header: t('admin.bateria.reasignar.employee'),
      cell: (e) => (
        <label className="flex items-center gap-2">
          <input
            type="radio"
            name="reasignar-target"
            checked={selected === e.id}
            onChange={() => setSelected(e.id)}
            data-testid={`reasignar-target-${e.id}`}
            className="accent-primary"
          />
          <span className="min-w-0 truncate font-medium text-foreground">
            {e.name} {e.last_name}
          </span>
          <span className="shrink-0 font-mono text-xs text-muted-foreground">{e.cedula}</span>
        </label>
      ),
    },
  ]

  return (
    <Dialog open={open} onOpenChange={(next) => !next && close()}>
      <DialogContent className="max-h-[85vh] max-w-xl overflow-y-auto" data-testid="reasignar-dialog">
        <DialogHeader>
          <DialogTitle>{t('admin.bateria.reasignar.title')}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4" noValidate>
          <p className="text-sm text-muted-foreground">
            {t('admin.bateria.reasignar.intro', {from: paquete.employee_name})}
          </p>
          <Input
            maxLength={tope('BUSCA')}
            value={search}
            onChange={(e) => {
              setSearch(e.target.value)
              setPage(1)
            }}
            placeholder={t('admin.bateria.reasignar.searchPlaceholder')}
            className="max-w-xs"
          />
          {!roster ? (
            <Skeleton className="h-40 w-full" data-testid="reasignar-cargando" />
          ) : (
            <>
              <DataTable
                columns={columns}
                rows={rows}
                rowKey={(e) => e.id}
                emptyState={
                  <div className="rounded-lg border border-dashed border-border px-4 py-8 text-center text-sm text-muted-foreground">
                    {candidates.length === 0
                      ? t('admin.bateria.reasignar.noneAvailable')
                      : t('admin.bateria.reasignar.noResults')}
                  </div>
                }
              />
              <PaginationBar page={page} pageSize={PAGE_SIZE} total={total} onPageChange={setPage} />
            </>
          )}
          {formaMismatch && (
            <div className="notice-amber rounded-lg px-4 py-2.5 text-sm" data-testid="reasignar-forma-warning">
              {t('admin.bateria.reasignar.formaWarning', {forma: paquete.forma})}
            </div>
          )}
          <FormDialogFooter
            error={error}
            pending={reasignar.isPending}
            submitLabel={t('admin.bateria.reasignar.submit')}
            onCancel={close}
          />
        </form>
      </DialogContent>
    </Dialog>
  )
}
